import React, { useEffect, useState } from "react";
import { Spinner, Modal, Button } from "react-bootstrap";
import {
  createAbout,
  deleteAbout,
  getAbout,
  updateAbout,
} from "../../services/Admin/adminServices";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";

const About = () => {
  const [infos, setInfos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();
  // Fetch data
  const fetchData = async () => {
    try {
      const response = await getAbout();
      setInfos(response?.data?.data?.abouts);
    } catch (error) {
      console.error("Error fetching about info:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Upload image to S3 and get URL from backend
  const uploadImageToS3 = async (file) => {
    if (!file) return null;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch("/api/admin/uploadImage", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Image upload failed");
      }

      const data = await response.json();
      return data.imageUrl || data.url;
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle image file selection and preview
  const handleImageChange = async (e, setFieldValue) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }

      setImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      const imageUrl = await uploadImageToS3(file);
      if (imageUrl) {
        setFieldValue("imageUrl", imageUrl);
        toast.success("Image uploaded successfully");
      }
    }
  };

  // Clear image preview and URL
  const handleClearImage = (setFieldValue) => {
    setImagePreview(null);
    setImageFile(null);
    setFieldValue("imageUrl", "");
  };

  // const handleEditClick = (info) => {
  //   setSelectedInfo(info);
  //   setImagePreview(info?.imageUrl || null);
  //   setIsEditMode(true);
  //   setShowModal(true);
  // };

  const handleEditClick = (info) => {
    navigate("/admin/about-form", {
      state: { info, isEditMode: true },
    });
  };
  // const handleAddNew = () => {
  //   setSelectedInfo(null);
  //   setImagePreview(null);
  //   setImageFile(null);
  //   setIsEditMode(false);
  //   setShowModal(true);
  // };

  const handleAddNew = () => {
    navigate("/admin/about-form", {
      state: { info: null, isEditMode: false },
    });
  };

  const handleFormSubmit = async (values) => {
    try {
      const payload = { ...values };
      if (!payload.title || payload.title.trim() === "") {
        delete payload.title;
      }

      if (isEditMode) {
        await updateAbout(payload);
        toast.success("Updated successfully");
      } else {
        await createAbout(payload);
        toast.success("Added successfully");
      }

      fetchData();
      handleCloseModal();
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred");
      console.error("Error:", error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAbout(id);
      fetchData();
      toast.success("Deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setImagePreview(null);
    setImageFile(null);
    setSelectedInfo(null);
    setIsEditMode(false);
  };

  const validationSchema = Yup.object({
    description: Yup.string().required("Description is required"),
    mentors: Yup.array().of(
      Yup.object({
        name: Yup.string().required("Mentor name is required"),
        degree: Yup.string().required("Mentor degree is required"),
      }),
    ),
  });

  const initialValues = isEditMode
    ? {
        aboutId: selectedInfo?._id || "",
        title: selectedInfo?.title || "",
        description: selectedInfo?.description || "",
        imageUrl: selectedInfo?.imageUrl || "",
        mentors: selectedInfo?.mentors || [{ name: "", degree: "" }],
      }
    : {
        title: "",
        description: "",
        imageUrl: "",
        mentors: [{ name: "", degree: "" }],
      };

  return (
    <div>
      <div className="d-flex justify-content-between mb-3">
        <h2 className="mb-3" style={{ color: "#87C55F" }}>
          About Us
        </h2>
        {infos.length > 0 ? (
          ""
        ) : (
          <div>
            <button
              className="btn"
              style={{
                backgroundColor: "#87C55F",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "4px",
              }}
              onClick={handleAddNew}
            >
              Add new
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <Spinner animation="border" variant="primary" role="status" />
        </div>
      ) : infos?.length === 0 ? (
        <p>No About available.</p>
      ) : (
        <ul className="list-group">
          {infos?.map((item) => (
            <li
              key={item._id}
              className="list-group-item d-flex flex-column"
              style={{ borderColor: "#87C55F" }}
            >
              <div className="d-flex justify-content-between">
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between">
                    <h2 className="fw-bold mb-1">{item.title}</h2>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm"
                        style={{
                          backgroundColor: "#87C55F",
                          color: "white",
                          border: "none",
                        }}
                        onClick={() => handleEditClick(item)}
                      >
                        Edit
                      </button>
                      {/* <button
                        className="btn btn-sm"
                        style={{
                          backgroundColor: "red",
                          color: "white",
                          border: "none",
                        }}
                        onClick={() => handleDelete(item._id)}
                      >
                        Delete
                      </button> */}
                    </div>
                  </div>
                  {item.image && (
                    <div className="mb-2">
                      <img
                        src={item.image}
                        alt={item.title}
                        style={{
                          maxWidth: "200px",
                          maxHeight: "150px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: item.description }} />
                  {item.mentors && item.mentors.length > 0 && (
                    <div className="mt-3">
                      <h5 className="fw-bold">Mentors:</h5>
                      <ul className="list-unstyled">
                        {item.mentors.map((mentor, index) => (
                          <li key={index}>
                            <strong>{mentor.name}</strong> - {mentor.degree}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Unified Modal for Add and Edit */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="titleColor">
            {isEditMode ? "Edit About" : "Add New About"}
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ isSubmitting, setFieldValue, values }) => (
            <Form>
              <Modal.Body>
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">
                    Title
                  </label>
                  <Field
                    name="title"
                    id="title"
                    className="form-control"
                    style={{ borderColor: "#87C55F" }}
                  />
                  <ErrorMessage
                    name="title"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="date" className="form-label">
                    Established On
                  </label>
                  <Field
                    type="date"
                    name="date"
                    id="date"
                    className="form-control"
                    style={{ borderColor: "#87C55F" }}
                  />
                  <ErrorMessage
                    name="date"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={values.description}
                    onChange={(content) =>
                      setFieldValue("description", content)
                    }
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        ["blockquote", "code-block"],
                        [{ header: 1 }, { header: 2 }],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ script: "sub" }, { script: "super" }],
                        [{ indent: "-1" }, { indent: "+1" }],
                        [{ direction: "rtl" }],
                        [{ size: ["small", false, "large", "huge"] }],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ color: [] }, { background: [] }],
                        [{ font: [] }],
                        [{ align: [] }],
                        ["clean"],
                        ["link", "image"],
                      ],
                    }}
                    style={{
                      height: "300px",
                      marginBottom: "20px",
                      borderColor: "#87C55F",
                    }}
                  />
                  <ErrorMessage
                    name="description"
                    component="div"
                    className="text-danger mt-2"
                  />
                </div>

                {/* Mentors Section */}

                {/* Image Upload Section */}
                <div className="mt-3">
                  <label className="form-label">Image</label>
                  <input
                    type="file"
                    id="image"
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, setFieldValue)}
                    disabled={uploadingImage}
                    style={{ borderColor: "#87C55F" }}
                  />
                  <small className="text-muted d-block mt-1">
                    Max file size: 5MB. Supported formats: JPG, PNG, GIF, WebP
                  </small>
                </div>

                {/* Image Preview */}
                {imagePreview && (
                  <div className="mb-3">
                    <label className="form-label">Image Preview</label>
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                        width: "100%",
                      }}
                    >
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          objectFit: "cover",
                          borderRadius: "4px",
                          border: "2px solid #87C55F",
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleClearImage(setFieldValue)}
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    {uploadingImage && (
                      <div className="mt-2">
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Uploading image...
                      </div>
                    )}
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-bold">Mentors</label>
                  <FieldArray name="mentors">
                    {({ push, remove }) => (
                      <div>
                        {values.mentors && values.mentors.length > 0
                          ? values.mentors.map((mentor, index) => (
                              <div key={index} className="card p-3 mb-2">
                                <div className="row">
                                  <div className="col-md-6">
                                    <label
                                      htmlFor={`mentors.${index}.name`}
                                      className="form-label"
                                    >
                                      Mentor Name
                                    </label>
                                    <Field
                                      name={`mentors.${index}.name`}
                                      id={`mentors.${index}.name`}
                                      className="form-control"
                                      placeholder="Enter mentor name"
                                      style={{ borderColor: "#87C55F" }}
                                    />
                                    <ErrorMessage
                                      name={`mentors.${index}.name`}
                                      component="div"
                                      className="text-danger small"
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label
                                      htmlFor={`mentors.${index}.degree`}
                                      className="form-label"
                                    >
                                      Degree
                                    </label>
                                    <Field
                                      name={`mentors.${index}.degree`}
                                      id={`mentors.${index}.degree`}
                                      className="form-control"
                                      placeholder="Enter degree"
                                      style={{ borderColor: "#87C55F" }}
                                    />
                                    <ErrorMessage
                                      name={`mentors.${index}.degree`}
                                      component="div"
                                      className="text-danger small"
                                    />
                                  </div>
                                </div>
                                {values.mentors.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger mt-2"
                                    onClick={() => remove(index)}
                                  >
                                    Remove Mentor
                                  </button>
                                )}
                              </div>
                            ))
                          : null}
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            backgroundColor: "#87C55F",
                            color: "white",
                            border: "none",
                          }}
                          onClick={() => push({ name: "", degree: "" })}
                        >
                          Add Mentor
                        </button>
                      </div>
                    )}
                  </FieldArray>
                </div>

                {/* Display current imageUrl if no new preview */}
                {!imagePreview && values.imageUrl && (
                  <div className="mb-3">
                    <label className="form-label">Current Image</label>
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                        width: "100%",
                      }}
                    >
                      <img
                        src={values.imageUrl}
                        alt="Current"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          objectFit: "cover",
                          borderRadius: "4px",
                          border: "2px solid #87C55F",
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleClearImage(setFieldValue)}
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  style={{ backgroundColor: "#87C55F", border: "none" }}
                  disabled={isSubmitting || uploadingImage}
                >
                  {isSubmitting
                    ? isEditMode
                      ? "Updating..."
                      : "Creating..."
                    : "Save"}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default About;
